// src/services/global-settings.ts
import api from "./api";
import { GlobalSettings } from "../types/global-settings";

export const globalSettingsService = {
  // Get all global settings
  getGlobalSettings: async (): Promise<{ data: GlobalSettings }> => {
    const response = await api.get("/global-settings");
    // The API returns { success: true, data: {...} }, so we return response.data.data
    // But let's return the full response to match the expected structure
    return response.data;
  },

  // Update school information
  updateSchoolInformation: async (data: any): Promise<{ data: any }> => {
    const response = await api.put("/global-settings/school-information", data);
    return response.data; // This will be { success: true, data: updatedSchoolInfo }
  },

  // Update academic year
  updateAcademicYear: async (data: any): Promise<{ data: any }> => {
    const response = await api.put("/global-settings/academic-year", data);
    return response.data;
  },

  // Update contact information
  updateContactInformation: async (data: any): Promise<{ data: any }> => {
    const response = await api.put(
      "/global-settings/contact-information",
      data
    );
    return response.data;
  },

  // Update financial settings
  updateFinancialSettings: async (data: any): Promise<{ data: any }> => {
    const response = await api.put("/global-settings/financial-settings", data);
    return response.data;
  },

  // Update academic configuration
  updateAcademicConfiguration: async (data: any): Promise<{ data: any }> => {
    const response = await api.put(
      "/global-settings/academic-configuration",
      data
    );
    return response.data;
  },

  // Update system settings
  updateSystemSettings: async (data: any): Promise<{ data: any }> => {
    const response = await api.put("/global-settings/system-settings", data);
    return response.data;
  },

  // Update notification settings
  updateNotificationSettings: async (data: any): Promise<{ data: any }> => {
    const response = await api.put(
      "/global-settings/notification-settings",
      data
    );
    return response.data;
  },

  // Upload logo
  uploadLogo: async (file: File): Promise<{ data: { url: string } }> => {
    const formData = new FormData();
    formData.append("logo", file);
    const response = await api.post("/global-settings/upload-logo", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // Upload favicon
  uploadFavicon: async (file: File): Promise<{ data: { url: string } }> => {
    const formData = new FormData();
    formData.append("favicon", file);
    const response = await api.post(
      "/global-settings/upload-favicon",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Test email configuration
  testEmailConfiguration: async (
    config: any
  ): Promise<{ data: { success: boolean; message: string } }> => {
    const response = await api.post("/global-settings/test-email", config);
    return response.data;
  },

  // Test SMS configuration
  testSmsConfiguration: async (
    config: any
  ): Promise<{ data: { success: boolean; message: string } }> => {
    const response = await api.post("/global-settings/test-sms", config);
    return response.data;
  },

  // Get system backup
  createSystemBackup: async (): Promise<{
    data: { backupId: string; downloadUrl: string };
  }> => {
    const response = await api.post("/global-settings/create-backup");
    return response.data;
  },

  // Reset to default settings
  resetToDefaults: async (section: string): Promise<{ data: any }> => {
    const response = await api.post(
      `/global-settings/reset-defaults/${section}`
    );
    return response.data;
  },
};
